<template>
  <!-- Create Item Modal -->
  <b-modal
    :visible="$props.show"
    title="Create New Item"
    size="xl"
    :hide-footer="true"
    :hide-header="true"
    :hide-header-close="true"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    centered
    id="create-customer-modal"
    body-class="p-0 overflow-hidden rounded-md"
  >
    <!-- Modal Body -->
    <div class="modal-body p-0">
      <items-form
        :form-data.sync="formData"
        :loading="isLoading"
        @submit="handleSubmit"
        :is-modal="true"
        @cancel="onCancel"
      />
    </div>

    <template #modal-footer>
      <b-button @click="handleTest">TEST</b-button>
    </template>
  </b-modal>
</template>

<script>
import { BModal, BButton, BSpinner } from 'bootstrap-vue';
import { computed, ref, watch } from '@vue/composition-api';
import { useToast } from 'vue-toastification/composition';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import ItemsForm from './ItemsForm.vue';
import store from '@/store';
import ItemsStoreModule from './ItemsStoreModule';

export default {
  name: 'CreateCustomerModal',
  components: {
    BModal,
    BButton,
    BSpinner,
    FeatherIcon,
    ItemsForm,
    ToastificationContent,
  },
  props: {
    show: {
      type: Boolean,
      required: true,
    },
  },
  setup(props, { emit }) {
    const toast = useToast();
    const ITEMS_STORE_MODULE_NAME = 'items';

    // Register module if not already registered
    if (!store.hasModule(ITEMS_STORE_MODULE_NAME)) {
      store.registerModule(ITEMS_STORE_MODULE_NAME, ItemsStoreModule);
    }

    // Reactive state
    const isLoading = ref(false);
    const formData = ref(null);

    const businessID = computed(() => store.state.user.businessID);
    const userID = computed(() => store.state.user.userID);

    const handleSubmit = async () => {
      // Validate NTN or CNIC requirement
      console.log('=== Items Add On Submit', JSON.stringify(formData.value, null, 2));

      isLoading.value = true;

      try {
        // Prepare payload with itemsFBR wrapper and userID
        const payload = {
          itemsFBR: {
            id: 0,
            itemCode: formData.value.itemCode,
            itemName: formData.value.itemName,
            unitID: formData.value.unitID,
            itemGroupID: formData.value.itemGroupID,
            itemPurchaseRate: formData.value.itemPurchaseRate,
            tradePrice: formData.value.tradePrice,
            retailPrice: formData.value.retailPrice,
            salesTaxPercent: formData.value.salesTaxPercent,
            addSalesTaxPercent: formData.value.addSalesTaxPercent,
            itemHSCodeID: formData.value.itemHSCodeID,
            isActive: formData.value.isActive,
            businessID: businessID.value,
          },
          userID: userID.value,
        };

        console.log('Payload:', payload);

        // Direct API call
        const createdItemId = await store.dispatch('items/saveItem', payload);
        console.log('=== CreateItemModal API Response:', createdItemId);

        if (!createdItemId) {
          // Show error message
          toast({
            component: ToastificationContent,
            props: {
              title: 'Error',
              text: 'Failed to create item. Please try again.',
              variant: 'danger',
              icon: 'AlertTriangleIcon',
            },
          });

          return;
        }

        // Reset form data
        resetFormData();

        // Close modal
        emit('update:show', false);

        // Emit submit event to parent
        emitItem(createdItemId);
      } catch (error) {
        console.error('Error creating item:', error);

        // Show error message
        toast({
          component: ToastificationContent,
          props: {
            title: 'Error',
            text: error.message || 'Failed to create item. Please try again.',
            variant: 'danger',
            icon: 'AlertTriangleIcon',
          },
        });
      } finally {
        isLoading.value = false;
      }
    };

    const resetFormData = () => {
      formData.value = {
        id: 0,
        code: '',
        name: '',
        shortName: '',
        cellNumber: '',
        category: '',
        email: '',
        ntnNumber: '',
        gstNumber: '',
        address: '',
        province: '',
        cnicNumber: '',
        contactPerson: '',
        whatsappOrUAN: '',
        website: '',
        commissionPercent: 0,
        isActive: true,
      };
    };

    const emitItem = (itemId) => {
      console.log('=== CreateItemModal emitItem', itemId);

      store
        .dispatch('items/fetchItemsByIds', [itemId])
        .then((item) => {
          console.log('=== CreateItemModal handleTest item', item[0]);
          console.log('=== ITEM ID:', item[0]);

          if (Array.isArray(item)) {
            emit('update:show', false);
            emit('submit', item[0]);
          } else {
            emit('update:show', false);
            emit('submit', null);
          }
        })
        .catch((error) => {
          console.error('Error creating item:', error);
          emit('update:show', false);
          emit('submit', null);
        });
    };

    const handleTest = () => {
      console.log('=== CreateItemModal handleTest');
      store
        .dispatch('items/fetchItemsByIds', [22])
        .then((item) => {
          console.log('=== CreateItemModal handleTest item', item[0]);
          console.log('=== ITEM ID:', item[0]);

          if (Array.isArray(item)) {
            emit('update:show', false);
            emit('submit', item[0]);
          } else {
            emit('update:show', false);
            emit('submit', null);
          }
        })
        .catch((error) => {
          console.error('Error creating item:', error);
          emit('update:show', false);
          emit('submit', null);
        });
    };

    const onCancel = () => {
      emit('update:show', false);
    };

    return {
      // Reactive state
      isLoading,
      formData,

      // Methods
      handleSubmit,
      handleTest,
      onCancel,
    };
  },
};
</script>

<style lang="scss" scoped>
.modal-body {
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
}

::v-deep .modal-xl {
  max-width: 95%;
}

::v-deep .modal-header {
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

::v-deep .modal-footer {
  border-top: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}
</style>
